import { ApolloError } from '@apollo/client/errors';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import {
  requestForSignature as requestForSignatureOnDocument,
  requestForSignatureForFile,
} from 'api/operations/documentsService';
import { requestForSignature as requestForSignatureOnPolicy } from 'api/operations/policyService';
import { retrieveNetworkErrorFromApollo } from 'global/utils/retrieveNetworkErrorFromApollo';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { File } from 'types/policyService';

export interface RequestSignatureContext {
  documentId: string;
  periodId: string;
  /*
   * This logic is available in places where we don't yet have a file for signature in our system (document service).
   * In such a case, the file must be passed and submission logic handled differently.
   */
  withFile?: File;
  refreshData: () => Promise<void> | void;
}

interface Fields extends DefaultFormFields {
  name: FormControl<string>;
  email: FormControl<string>;
}

export class RequestSignatureModalStore {
  public modalDelegate: ModalDelegate = new ModalDelegate();
  private _isLoading = false;
  private readonly _contextualData: RequestSignatureContext;
  private _backEndEmailValidationError: null | string = null;
  public readonly form = new FormController<Fields>({
    name: new FormControl('', {
      validations: ['printableCharacters'],
      optional: false,
    }),
    email: new FormControl('', { validations: ['isEmailValid'], optional: false }),
  });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get backEndEmailValidationError(): null | string {
    return this._backEndEmailValidationError;
  }

  public resetForm(): void {
    this.form.resetForm();
    this._backEndEmailValidationError = null;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setBackEndEmailValidationError(error: string | null): void {
    this._backEndEmailValidationError = error;
  }

  private async _handleSubmit(): Promise<void> {
    this.setBackEndEmailValidationError(null);
    const recipient = {
      name: this.form.fields.name.value.trim(),
      email: this.form.fields.email.value.trim(),
    };
    if (this._contextualData.withFile) {
      await requestForSignatureForFile(this._contextualData.periodId, recipient, this._contextualData.withFile);
      await requestForSignatureOnPolicy(this._contextualData.withFile, this._contextualData.periodId);
    } else {
      await requestForSignatureOnDocument(this._contextualData.documentId, recipient);
    }
  }

  public async sendSignatureRequest(): Promise<void> {
    this.setIsLoading(true);

    try {
      await this._handleSubmit();
    } catch (e) {
      this.setBackEndEmailValidationError(retrieveNetworkErrorFromApollo(e as ApolloError));
      this.setIsLoading(false);
      return;
    }

    await runInAction(async () => {
      this.modalDelegate.close();
      this.setIsLoading(false);
      await this._contextualData.refreshData();
    });
  }

  constructor(props: RequestSignatureContext) {
    this._contextualData = props;
    makeObservable<RequestSignatureModalStore, '_isLoading' | '_handleSubmit' | '_backEndEmailValidationError'>(this, {
      _isLoading: observable,
      _backEndEmailValidationError: observable,
      modalDelegate: observable,
      form: observable,
      isLoading: computed,
      backEndEmailValidationError: computed,
      _handleSubmit: action.bound,
      setIsLoading: action.bound,
      sendSignatureRequest: action.bound,
      setBackEndEmailValidationError: action.bound,
      resetForm: action.bound,
    });
  }
}
